import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CircularProgress, Step, StepLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';

import Button from 'src/presentations/components/atom/button';
import { ModalConfirmCustom, StyledModal } from 'src/presentations/components/molecules/modal';

import format from 'date-fns/format';
import {
  useContractChargesDetailsForShippingService,
  useCreateShippingServiceAndContract,
  useIsValidityPeriodValid,
  useUpdateNoContractDraft,
} from 'src/use-cases/invoice/useNoContract';
import { NewShippingServiceDto, NoContractDraftData } from 'src/generated/services/TFinancialApi';
import { usePerformShipmentWorkflowAction } from 'src/use-cases/invoice/useInvoices';
import { DeviationWorkflowAction } from 'src/infra/types/deviation';
import { API_DATE_FORMAT } from 'src/config';
import useToast from 'src/use-cases/toast/useToast';
import { INoContractMainProps, ModalOpen, NoContractForm, NoContractStep, SelectedShippingServiceItem } from './types';
import { NoContractStepIcon } from './StepIcon';
import { NoContractHeader } from './Header';
import { NoContractContent } from './Content';
import { NoContractFooter } from './Footer';
import { useShipmentSelector } from '../ShipmentSelector';
import { useRecalculationToastSelector } from '../recalculationToast/RecalculationToastSelector';

const StyledStepper = styled(Stepper)({
  '& .MuiStepConnector-root': {
    padding: '0 8px',
  },
  '& .MuiStepConnector-line': {
    borderTopStyle: 'dashed',
    borderTopWidth: '2px',
  },
  '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
    borderTopStyle: 'solid',
    borderColor: '#00992B',
  },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
    color: '#00992B',
    fontWeight: 'bold',
  },
  '& .MuiStepIcon-root': {
    color: '#ffffff',
    width: '32px',
    height: '32px',
    border: '1px solid #DFDFDF',
    borderRadius: '100%',

    '& .MuiStepIcon-text': {
      fill: '#00992B',
      fontWeight: 'bold',
    },

    '&.Mui-active': {
      color: '#00992B',
      borderColor: '#00992B',

      '& .MuiStepIcon-text': {
        fill: '#ffffff',
      },
    },
  },
});

const mismatchedFieldMap = {
  shipperName: 'shipper name',
  consigneeName: 'consignee name',
  service: 'service',
  incoterm: 'incoterm',
  type: 'type',
  carrier: 'carrier',
  listZone: 'listZone',
  pol: 'pol',
  pod: 'pod',
  route: 'route',
};

export const NoContractMain: FC<INoContractMainProps> = (props) => {
  const { matchingService, defaultContractFlowId, draft, invoiceSummary, onClose } = props;
  const { shipment } = useShipmentSelector();

  const [isShowModalConfirm, setIsShowModalConfirm] = useState<ModalOpen>(null);
  const [isShowErrorPeriod, setIsShowErrorPeriod] = useState<boolean>(false);
  const [activeStep, setActiveStep] = useState<NoContractStep>(NoContractStep.Step1_Select);
  const [contractFlowId, setContractFlowId] = useState<string>(defaultContractFlowId);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState<boolean>(false);
  const [isEditingCharge, setIsEditingCharge] = useState<boolean>(false);

  const { watch, setValue, resetField } = useFormContext<NoContractForm>();
  const formValues = watch();
  const { verdict, selectedPartialMatchingService, newMatchingService, contractChoice, reason } = formValues;

  useEffect(() => {
    if (defaultContractFlowId) {
      setContractFlowId(defaultContractFlowId);
    }
  }, [defaultContractFlowId]);

  const { isLoading: isLoadingToast, performWorkflowAction } = useRecalculationToastSelector();
  const { mutateAsync: updateDraft } = useUpdateNoContractDraft(shipment?.shipmentInvoiceId);
  const { mutateAsync: createShippingService } = useCreateShippingServiceAndContract(shipment?.shipmentInvoiceId);
  const { mutateAsync: performShipmentWorkflowAction } = usePerformShipmentWorkflowAction();
  const { mutateAsync: checkIsValidityPeriodValid } = useIsValidityPeriodValid(invoiceSummary?.id);
  const openToast = useToast();

  const { data, isRefetching } = useContractChargesDetailsForShippingService(contractFlowId || null);

  useEffect(() => {
    if (data?.containerCharges) {
      const items = data.containerCharges.filter((check) => check?.chargeType);
      setValue('containerCharges', items);
    }
    if (data?.declarationCharges) {
      const items = data.declarationCharges.filter((check) => check?.chargeType);
      setValue('declarationCharges', items);
    }
    if (data?.shipmentCharges) {
      const items = data.shipmentCharges.filter((check) => check?.chargeType);
      setValue('shipmentDeclarationCharges', items);
    }
    if (data?.percentageCharges) {
      const items = data.percentageCharges
        .filter((check) => check?.chargeType)
        .map((item) => {
          const newItem = {
            ...item,
            validityPeriod: {
              start: item?.startDate ? new Date(item?.startDate) : undefined,
              end: item?.endDate ? new Date(item?.endDate) : undefined,
            },
          };
          return newItem;
        });
      setValue('percentageCharges', items);
    }
    if (data?.weightCharges) {
      const items = data.weightCharges.filter((check) => check?.chargeType);
      setValue('weightCharges', items);
    }
  }, [data, draft, setValue]);

  useEffect(() => {
    if (draft) {
      setActiveStep(draft?.data?.currentStep);
    }
  }, [draft]);

  // Reset editing state when step changes
  useEffect(() => {
    setIsEditingCharge(false);
  }, [activeStep]);

  useEffect(() => {
    if (verdict !== 'accept') {
      resetField('contractChoice');
    }
  }, [verdict, resetField]);

  useEffect(() => {
    // Find service by contractFlowId in contractInfos array
    const currentContractId = selectedPartialMatchingService?.contractInfos?.find((c) => c.contractFlowId === contractChoice)?.contractFlowId;
    if (!selectedPartialMatchingService || currentContractId !== contractChoice) {
      const chosenShippingService = matchingService?.partialMatchingServices.find((op) => op.contractInfos?.some((c) => c.contractFlowId === contractChoice));
      if (chosenShippingService) {
        setValue('selectedPartialMatchingService', chosenShippingService);
        // Set the selected contractInfo
        const selectedContract = chosenShippingService.contractInfos?.find((c) => c.contractFlowId === contractChoice);
        setValue('selectedContractInfo', selectedContract);
      }
    }
  }, [contractChoice, matchingService?.partialMatchingServices, selectedPartialMatchingService, setValue]);

  const getMatchingServiceForApi = useCallback(
    (matchingData: SelectedShippingServiceItem) => {
      let selectedData = null;
      if (matchingData?.existingShippingService) {
        const { validityPeriod, ...others } = matchingData.existingShippingService;

        // Get contractFlowId and listZone from selected contract in contractInfos
        const selectedContract = matchingData?.contractInfos?.find((c) => c.contractFlowId === contractChoice);

        // Build the shipping service data
        const shippingServiceData: any = {
          ...others,
          startDate: format(validityPeriod?.start, 'yyyy-MM-dd'),
          endDate: format(validityPeriod?.end, 'yyyy-MM-dd'),
        };

        // Only add contractFlowId if there's a contract selected (copy charge flow)
        if (selectedContract?.contractFlowId || contractChoice) {
          shippingServiceData.contractFlowId = selectedContract?.contractFlowId || contractChoice;
        }

        // Only override listZone if there's a selected contract with listZone (copy charge flow)
        // Otherwise, keep the listZone from others (skip flow)
        if (selectedContract?.listZone) {
          shippingServiceData.listZone = selectedContract.listZone;
        }

        selectedData = {
          existingShippingService: shippingServiceData,
          mismatchedFields: matchingData?.mismatchedFields,
        };
      }

      return selectedData;
    },
    [contractChoice]
  );

  const onSaveDraft = useCallback(async () => {
    // Use the original SelectedShippingServiceItem structure for draft
    // This preserves contractInfos array and original structure
    const selectedService = formValues.newMatchingService || formValues.selectedPartialMatchingService;

    const percentageCharges = formValues?.percentageCharges
      ? formValues.percentageCharges.map((item) => {
          const { validityPeriod, ...charge } = item;
          return {
            ...charge,
            startDate: validityPeriod?.start ? format(validityPeriod.start, API_DATE_FORMAT) : null,
            endDate: validityPeriod?.end ? format(validityPeriod.end, API_DATE_FORMAT) : null,
          };
        })
      : [];
    const apiData = {
      selectedPartialMatchingService: selectedService, // Save original structure with contractInfos
      contractCharges: {
        containerCharges: formValues.containerCharges || [],
        declarationCharges: formValues.declarationCharges || [],
        shipmentDeclarationCharges: formValues.shipmentDeclarationCharges || [],
        weightCharges: formValues.weightCharges || [],
        percentageCharges,
      },
      currentStep: activeStep,
      selectedResolutionOption: verdict === 'setup-new' ? 'setup-new' : '',
      rejectedReason: reason || '',
    };

    await updateDraft(apiData as NoContractDraftData);
    onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    getMatchingServiceForApi,
    formValues.newMatchingService,
    formValues.containerCharges,
    formValues.shipmentDeclarationCharges,
    formValues.weightCharges,
    formValues.percentageCharges,
    verdict,
    reason,
    updateDraft,
    onClose,
    activeStep,
  ]);

  const onConfirm = useCallback(async () => {
    setIsLoadingConfirm(true);

    try {
      if (verdict === 'accept') {
        // Handle accept verdict: Accept as spot shipment
        const spotShipmentCharges =
          formValues.invoiceCharges?.map((charge) => ({
            id: charge.id,
            chargeCategory: charge.chargeCategory,
            legType: charge.legType,
            accepted: charge.decision ?? true,
          })) || [];

        // Check if any charge is rejected (decision === false)
        const hasRejectedCharges = formValues.invoiceCharges?.some((charge) => charge.decision === false) ?? false;

        await performShipmentWorkflowAction({
          shipmentInvoiceId: shipment?.shipmentInvoiceId,
          invoiceId: invoiceSummary?.id,
          deviationWorkflowAction: DeviationWorkflowAction.ACCEPT_AS_SPOT_SHIPMENT,
          spotShipmentCharges,
          reason: hasRejectedCharges ? reason : null,
        });
      } else {
        // Handle setup-new verdict: Create new shipping service
        const matchingService = getMatchingServiceForApi(formValues.newMatchingService);

        // Helper function to remove null id fields and filter out empty/incomplete charges
        const cleanCharges = (charges: any[]) => {
          // First filter out charges that are empty or incomplete
          const validCharges = charges.filter((charge) => {
            // A charge is considered valid if it has at least chargeName or chargeCategory filled with non-empty string
            const hasChargeName = charge.chargeName && charge.chargeName.trim() !== '';
            const hasChargeCategory = charge.chargeCategory && charge.chargeCategory.trim() !== '';
            return hasChargeName || hasChargeCategory;
          });

          // Then clean the IDs
          return validCharges.map(({ id, ...charge }) =>
            // Only include id if it's not null
            id ? { id, ...charge } : charge
          );
        };

        const percentageCharges = formValues?.percentageCharges
          ? formValues.percentageCharges
              .filter((item) => {
                // Filter out empty charges - must have chargeName or chargeCategory with non-empty string
                const hasChargeName = item.chargeName && item.chargeName.trim() !== '';
                const hasChargeCategory = item.chargeCategory && item.chargeCategory.trim() !== '';
                return hasChargeName || hasChargeCategory;
              })
              .map((item) => {
                const { validityPeriod, id, ...charge } = item;
                const cleanedCharge: any = {
                  ...charge,
                  startDate: validityPeriod?.start ? format(validityPeriod.start, API_DATE_FORMAT) : null,
                  endDate: validityPeriod?.end ? format(validityPeriod.end, API_DATE_FORMAT) : null,
                };
                // Only include id if it's not null
                if (id) cleanedCharge.id = id;
                return cleanedCharge;
              })
          : [];

        const apiData = {
          newShippingService: matchingService?.existingShippingService,
          contractCharges: {
            containerCharges: cleanCharges(formValues.containerCharges || []),
            declarationCharges: cleanCharges(formValues.declarationCharges || []),
            shipmentDeclarationCharges: cleanCharges(formValues.shipmentDeclarationCharges || []),
            weightCharges: cleanCharges(formValues.weightCharges || []),
            percentageCharges,
          },
        };

        const response = await createShippingService(apiData);
        if (response?.data?.success) {
          await performWorkflowAction({
            shipmentInvoiceId: shipment?.shipmentInvoiceId,
            invoiceId: invoiceSummary?.id,
            deviationWorkflowAction: DeviationWorkflowAction.ACCEPT_INVOICE,
          });
        }
      }

      setIsLoadingConfirm(false);
      setIsShowModalConfirm(null);
      onClose();
    } catch {
      setIsLoadingConfirm(false);
      setIsShowModalConfirm(null);
      setIsShowErrorPeriod(true);
      setActiveStep(activeStep);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    verdict,
    getMatchingServiceForApi,
    formValues.newMatchingService,
    formValues.containerCharges,
    formValues.shipmentDeclarationCharges,
    formValues.weightCharges,
    formValues.percentageCharges,
    formValues.invoiceCharges,
    createShippingService,
    formValues.reason,
    performShipmentWorkflowAction,
    shipment?.shipmentInvoiceId,
    invoiceSummary?.id,
    onClose,
  ]);

  const onSuccessSubmit = async () => {
    if (activeStep === NoContractStep.Step1_Select || (activeStep === NoContractStep.Step2_Resolution && verdict === 'reject')) {
      setActiveStep(activeStep + 1);
      return;
    }

    if (activeStep === NoContractStep.Step2_Resolution && verdict === 'setup-new') {
      // Populate newMatchingService before moving to Step2b_NewService
      // Check if user selected a contract (Copy Charge) or skipped
      const chosenShippingService = contractChoice
        ? matchingService?.partialMatchingServices.find((op) => op.contractInfos?.some((c) => c.contractFlowId === contractChoice))
        : null;

      if (chosenShippingService && contractChoice) {
        // User clicked "Copy Charge" - merge mismatched fields from unmatchedService
        const existingItems = chosenShippingService?.existingShippingService;
        const newServiceData = {};

        for (const key in existingItems) {
          let value = null;
          switch (key) {
            case 'listZone':
              if (
                typeof mismatchedFieldMap[key] !== 'undefined' &&
                (chosenShippingService.mismatchedFields.includes('pol') || chosenShippingService.mismatchedFields.includes('pod')) &&
                existingItems[key] !== null &&
                existingItems[key]?.length > 0
              ) {
                value = matchingService?.unmatchedService[key];
              } else {
                value = existingItems[key] || null;
              }
              break;
            case 'route':
              if (
                typeof mismatchedFieldMap[key] !== 'undefined' &&
                chosenShippingService.mismatchedFields.includes('route') &&
                existingItems[key] !== null &&
                existingItems[key] !== ''
              ) {
                if (typeof matchingService?.unmatchedService[key] !== 'undefined') {
                  value = matchingService?.unmatchedService[key];
                } else {
                  value = matchingService?.unmatchedService?.listZone?.map((item) => `${item?.pol}:${item?.pod}`)?.join(', ');
                }
              } else {
                value = existingItems[key] || null;
              }
              break;
            case 'shipperName':
            case 'consigneeName':
            case 'service':
            case 'type':
            case 'carrier':
            case 'incoterm':
              if (
                typeof mismatchedFieldMap[key] !== 'undefined' &&
                chosenShippingService.mismatchedFields.includes(mismatchedFieldMap[key]) &&
                existingItems[key] !== null &&
                existingItems[key] !== ''
              ) {
                value = matchingService?.unmatchedService[key];
              } else {
                value = existingItems[key] || null;
              }
              break;
            default:
              value = existingItems[key] || null;
          }

          newServiceData[key] = value;
        }

        const selectedContract = chosenShippingService.contractInfos?.find((c) => c.contractFlowId === contractChoice);
        const newShippingServiceData = {
          mismatchedFields: chosenShippingService?.mismatchedFields,
          contractInfos: chosenShippingService?.contractInfos,
          existingShippingService: {
            ...chosenShippingService.existingShippingService,
            ...newServiceData,
            validityPeriod: selectedContract
              ? {
                  start: new Date(selectedContract.startDate),
                  end: new Date(selectedContract.endDate),
                }
              : undefined,
          },
        };
        setValue('newMatchingService', newShippingServiceData);
      } else if (matchingService?.unmatchedService) {
        // Handle "Skip" case - populate newMatchingService with unmatchedService data
        // Map MatchingServiceDto fields to SelectedShippingServiceItemMain fields
        const unmatchedSvc = matchingService.unmatchedService;
        const newShippingServiceData: SelectedShippingServiceItem = {
          mismatchedFields: [],
          contractInfos: [],
          existingShippingService: {
            route: unmatchedSvc.shippingZone, // Map shippingZone to route
            shipperName: unmatchedSvc.shipperName,
            consigneeName: unmatchedSvc.consigneeName,
            billingPartyCode: unmatchedSvc.billingPartyCode,
            modality: unmatchedSvc.modality,
            service: unmatchedSvc.service,
            type: unmatchedSvc.type,
            carrier: unmatchedSvc.carrier,
            incoterm: unmatchedSvc.incoterm,
            listZone: unmatchedSvc.listZone,
            validityPeriod: undefined, // Will be set by user in Step2b date pickers
          },
        };
        setValue('newMatchingService', newShippingServiceData);
      }

      setActiveStep(NoContractStep.Step2b_NewService);
      return;
    }

    if (activeStep === NoContractStep.Step2b_NewService && verdict === 'setup-new') {
      let isValid = true;
      if (formValues.newMatchingService?.existingShippingService) {
        // eslint-disable-next-line no-unsafe-optional-chaining
        const { validityPeriod, ...others } = formValues.newMatchingService?.existingShippingService;

        // Check if dates are filled
        if (!validityPeriod?.start || !validityPeriod?.end) {
          openToast({
            toastType: 'error',
            title: 'Missing Validity Period',
            description: 'Please select both Start Date and End Date before proceeding.',
          });
          return;
        }

        // Get listZone from contractInfos (selected contract)
        const selectedContract = formValues.newMatchingService?.contractInfos?.find((c) => c.contractFlowId === contractChoice);

        const verifyData: NewShippingServiceDto = {
          ...others,
          // Only override listZone if there's a selected contract with listZone (copy charge flow)
          // Otherwise, keep the listZone from others (skip flow)
          listZone: selectedContract?.listZone || others.listZone || undefined,
          startDate: format(new Date(validityPeriod.start), API_DATE_FORMAT),
          endDate: format(new Date(validityPeriod.end), API_DATE_FORMAT),
        };
        const checkResponse = await checkIsValidityPeriodValid(verifyData);
        isValid = !!checkResponse?.data?.data?.isValid;
      }

      if (!isValid) {
        setIsShowErrorPeriod(true);
      } else {
        setActiveStep(NoContractStep.Step3_Summary);
      }
      return;
    }

    if (activeStep === NoContractStep.Step2_Resolution && verdict === 'accept') {
      const invoiceCharges = formValues.invoiceCharges || [];
      const hasInvoiceCharges = shipment?.invoiceCharges && shipment.invoiceCharges.length > 0;

      if (hasInvoiceCharges) {
        const incompleteCharges = invoiceCharges.filter((charge) => !charge.chargeCategory || !charge.legType);

        if (incompleteCharges.length > 0 || invoiceCharges.length !== shipment.invoiceCharges.length) {
          openToast({
            toastType: 'error',
            title: 'Incomplete Charge Information',
            description: 'Please select Charge Category and Leg Type for all charges before proceeding.',
          });
          return;
        }
      }

      setActiveStep(NoContractStep.Step3_Summary);
    }
  };

  const onReject = useCallback(async () => {
    await performWorkflowAction({
      shipmentInvoiceId: shipment?.shipmentInvoiceId,
      invoiceId: invoiceSummary?.id,
      deviationWorkflowAction: DeviationWorkflowAction.REJECT_INVOICE,
      reason,
    });

    onClose();
  }, [performWorkflowAction, shipment?.shipmentInvoiceId, invoiceSummary?.id, reason, onClose]);

  const onGoBack = useCallback(async () => {
    if (activeStep === NoContractStep.Step2b_NewService) {
      setActiveStep(NoContractStep.Step2_Resolution);
    } else {
      // When going back to Step 1, reset all selections
      if (activeStep === NoContractStep.Step2_Resolution) {
        setValue('verdict', null);
        setValue('contractChoice', null);
        setValue('selectedPartialMatchingService', null);
        setValue('selectedContractInfo', null);
        setValue('newMatchingService', null);
      }
      setActiveStep(activeStep - 1);
    }
  }, [activeStep, setValue]);

  const steps = useMemo(() => {
    const items = [
      { id: NoContractStep.Step1_Select, label: 'Selection' },
      { id: NoContractStep.Step2_Resolution, label: 'Resolution' },
    ];

    if (!verdict || verdict !== 'accept') {
      items.push({ id: NoContractStep.Step3_Summary, label: 'Summary' });
    }

    return items;
  }, [verdict]);

  return (
    <div className='pb-[100px]'>
      <div className='px-24 pb-20 border-neutral-20'>
        <div className='py-12 px-24 bg-neutral-10 rounded-8 mb-20'>
          <StyledStepper activeStep={activeStep === NoContractStep.Step2b_NewService ? NoContractStep.Step2_Resolution : activeStep}>
            {steps.map(({ id, label }) => (
              <Step key={id}>
                <StepLabel color='inherit' StepIconComponent={NoContractStepIcon}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>
        </div>

        <NoContractHeader activeStep={activeStep} />
      </div>

      <NoContractContent
        activeStep={activeStep}
        matchingService={matchingService}
        draft={draft}
        isLoadingCharges={isRefetching}
        billedEntityId={invoiceSummary?.billedPartyId}
        onSuccessSubmit={onSuccessSubmit}
        onEditingChargeChange={setIsEditingCharge}
      />

      <NoContractFooter
        activeStep={activeStep}
        onGoBack={onGoBack}
        onClose={onClose}
        onSuccessSubmit={onSuccessSubmit}
        onSaveDraft={onSaveDraft}
        isEditingCharge={isEditingCharge}
        onConfirm={(e: NoContractForm) => {
          if (e.verdict === 'accept') {
            setIsShowModalConfirm('create');
          } else if (e.verdict === 'setup-new') {
            setIsShowModalConfirm('create');
          } else if (e.verdict === 'reject') {
            setIsShowModalConfirm('reject');
          }
        }}
        onSkip={() => {
          // Populate newMatchingService with unmatchedService data for skip flow
          if (matchingService?.unmatchedService) {
            const unmatchedSvc = matchingService.unmatchedService;
            const newShippingServiceData: SelectedShippingServiceItem = {
              mismatchedFields: [],
              contractInfos: [],
              existingShippingService: {
                route: unmatchedSvc.shippingZone, // Map shippingZone to route
                shipperName: unmatchedSvc.shipperName,
                consigneeName: unmatchedSvc.consigneeName,
                billingPartyCode: unmatchedSvc.billingPartyCode,
                modality: unmatchedSvc.modality,
                service: unmatchedSvc.service,
                type: unmatchedSvc.type,
                carrier: unmatchedSvc.carrier,
                incoterm: unmatchedSvc.incoterm,
                listZone: unmatchedSvc.listZone,
                validityPeriod: undefined, // Will be set by user in Step2b date pickers
              },
            };
            setValue('newMatchingService', newShippingServiceData);
          }

          // Clear all charges for skip flow
          setValue('containerCharges', []);
          setValue('declarationCharges', []);
          setValue('shipmentDeclarationCharges', []);
          setValue('weightCharges', []);
          setValue('percentageCharges', []);

          setActiveStep(NoContractStep.Step2b_NewService);
        }}
      />
      <ModalConfirmCustom
        open={isShowModalConfirm === 'create'}
        title='Confirmation'
        onAccept={onConfirm}
        onCancel={() => {
          setIsShowModalConfirm(null);
        }}
        isLoading={isLoadingConfirm || isLoadingToast}
        buttonAcceptLabel='Reject'
        buttonChildren={
          <>
            <Button
              variant='secondary'
              disabled={isLoadingConfirm || isLoadingToast}
              onClick={() => {
                setIsShowModalConfirm(null);
              }}
            >
              Cancel
            </Button>
            <Button variant='primary' onClick={onConfirm} disabled={isLoadingConfirm || isLoadingToast}>
              <div className='flex items-center gap-6'>
                Confirm
                {(isLoadingConfirm || isLoadingToast) && <CircularProgress size={20} sx={{ marginLeft: '1em', color: '#ffffff' }} />}
              </div>
            </Button>
          </>
        }
      >
        <p>
          {verdict === 'accept'
            ? 'Are you sure you want to accept this as spot shipment?'
            : 'You will create a new shipping service in the system with the corresponding Rate Card charges that you reviewed and trigger the audit of the invoice. Are you sure?'}
        </p>
      </ModalConfirmCustom>

      <ModalConfirmCustom
        open={isShowModalConfirm === 'reject'}
        title='Confirmation'
        onAccept={onReject}
        onCancel={() => {
          setIsShowModalConfirm(null);
        }}
        isLoading={isLoadingToast}
        buttonAcceptLabel='Reject'
        buttonChildren={
          <>
            <Button
              variant='secondary'
              disabled={isLoadingToast}
              onClick={() => {
                setIsShowModalConfirm(null);
              }}
            >
              Cancel
            </Button>
            <Button className='bg-[#E72547]' variant='danger' onClick={onReject} disabled={isLoadingToast}>
              Reject
            </Button>
          </>
        }
      >
        <p>Are you sure you want to reject this shipment?</p>
      </ModalConfirmCustom>

      <StyledModal open={isShowErrorPeriod} title='Warning: Validity Period' fullWidth onClose={() => setIsShowErrorPeriod(false)}>
        <div className='relative'>
          <ol className='list-decimal px-3'>
            <li>Make sure the new Validity Period covers the ETD of the current invoice.</li>
            <li>Make sure the new Validity Period doesn’t overlap with existing Validity Periods of similar Shipping Services.</li>
          </ol>

          <div className='mt-48'>
            <Button variant='primary' onClick={() => setIsShowErrorPeriod(false)} isFull>
              Dismiss
            </Button>
          </div>
        </div>
      </StyledModal>
    </div>
  );
};
